import type { z } from "zod/v4";
import {
	leagueMatchListResponseSchema,
	leagueMatchRevisionListResponseSchema,
	leagueMatchSchema,
	type leagueMatchStatusSchema,
} from "../../schemas/leagues";
import type { EditLeagueMatch, EnterLeagueResult, ResolveLeagueMatch } from "../../types";
import { fetchApi } from "../internal";

const DEFAULT_LIMIT = 30;

type ListMatchesParams = {
	status?: z.infer<typeof leagueMatchStatusSchema>;
	seasonId?: string;
	limit?: number;
	before?: string;
};

export const matches = {
	// Replace a result's sets (and optionally its date) — works in a closed
	// season too. The roster can't change: void it and enter a new one.
	edit: (leagueId: string, matchId: string, data: EditLeagueMatch) =>
		fetchApi(`/leagues/${leagueId}/matches/${matchId}`, leagueMatchSchema, {
			body: JSON.stringify(data),
			method: "PUT",
		}),
	// Staff entering a result themselves (e.g. from a paper sheet) — need not
	// be a player; `season_id` may name a closed season of this league.
	enterResult: (leagueId: string, data: EnterLeagueResult) =>
		fetchApi(`/leagues/${leagueId}/matches`, leagueMatchSchema, {
			body: JSON.stringify({ ...data, as_staff: true }),
			method: "POST",
		}),
	get: (leagueId: string, matchId: string) =>
		fetchApi(`/leagues/${leagueId}/matches/${matchId}`, leagueMatchSchema),
	// Newest first; pass the previous page's `next_before` back in as `before`.
	list: (leagueId: string, params?: ListMatchesParams) => {
		const searchParams = new URLSearchParams();
		if (params?.status) searchParams.set("status", params.status);
		if (params?.seasonId) searchParams.set("season_id", params.seasonId);
		searchParams.set("limit", String(params?.limit ?? DEFAULT_LIMIT));
		if (params?.before) searchParams.set("before", params.before);
		return fetchApi(
			`/leagues/${leagueId}/matches?${searchParams.toString()}`,
			leagueMatchListResponseSchema,
		);
	},
	revisions: {
		// Oldest first — staff only.
		list: (leagueId: string, matchId: string) =>
			fetchApi(
				`/leagues/${leagueId}/matches/${matchId}/revisions`,
				leagueMatchRevisionListResponseSchema,
			),
	},
	uphold: (leagueId: string, matchId: string, data?: ResolveLeagueMatch) =>
		fetchApi(`/leagues/${leagueId}/matches/${matchId}/uphold`, leagueMatchSchema, {
			body: JSON.stringify(data ?? {}),
			method: "POST",
		}),
	void: (leagueId: string, matchId: string, data?: ResolveLeagueMatch) =>
		fetchApi(`/leagues/${leagueId}/matches/${matchId}/void`, leagueMatchSchema, {
			body: JSON.stringify(data ?? {}),
			method: "POST",
		}),
};
